import type { CopyPaste } from 'handsontable/plugins/copyPaste';
import { MNHotInstance } from '../../tableHotInstanceTypes';
import platformSingleton from '../../../../platform/platformSingleton';
import { isPlatformCapacitor, isPlatformSafari } from '../../../../platform/utils/platformDetailsUtils';
import { readFromNativeClipboard, writeToNativeClipboard } from '../../../../capacitor/utils/capacitorClipboardUtils';

const TIPTAP_RENDERER_SELECTOR = '.TableTiptapCellRenderer .ProseMirror[contenteditable="false"]';
const EMPTY_CELL_PASTE_HTML = '<table><tbody><tr><td></td></tr></tbody></table>';

/**
 * Reads clipboard text from Capacitor's native clipboard bridge or the browser clipboard, resolving
 * with null when clipboard access is unavailable or denied. Exported so menu actions can start the
 * read inside the click's transient user activation, before any deferred menu-close choreography.
 */
export const readTableClipboardText = (): Promise<string | null> => {
    try {
        const clipboardText = isPlatformCapacitor(platformSingleton)
            ? readFromNativeClipboard()
            : navigator.clipboard.readText();

        return clipboardText.catch(() => null);
    } catch {
        // navigator.clipboard itself is missing in insecure contexts and older WebViews.
        return Promise.resolve(null);
    }
};

const getClipboardPlugin = (hotInstance: MNHotInstance | null): CopyPaste | undefined =>
    hotInstance?.getPlugin('copyPaste') as CopyPaste | undefined;

/**
 * Runs a clipboard action with Safari's required temporary Tiptap renderer editability override.
 */
const runWithSafariClipboardWorkaround = (hotInstance: MNHotInstance, clipboardFn: () => void): void => {
    const { row, col } = hotInstance.getSelectedRangeActive()?.highlight || {};
    const selectedCell = row === undefined || col === undefined ? null : hotInstance.getCell(row, col, true);
    const tiptapRenderer = selectedCell?.querySelector<HTMLElement>(TIPTAP_RENDERER_SELECTOR);

    // In Safari, programmatically triggered copy/cut actions can target the context menu rather than
    // the selected table cell, causing Handsontable to ignore the clipboard event.
    // - Handsontable works around this by temporarily making the selected TD editable before triggering
    //   the browser action.
    // - Our nested read-only Tiptap renderer also needs to be temporarily editable so Safari keeps the event target
    //   inside the selected table cell.
    tiptapRenderer?.setAttribute('contenteditable', 'true');

    // In Safari, programmatically triggered copy/cut actions do not emit a clipboard event when the selected cell
    // has no selectable content. The fix is to temporarily add a zero-width space while it triggers the browser action.
    const isEmptyCell = selectedCell && !selectedCell.textContent?.trim();
    const emptyCellCopyTarget = isEmptyCell ? selectedCell.ownerDocument.createTextNode('\u200B') : null;
    emptyCellCopyTarget && selectedCell?.appendChild(emptyCellCopyTarget);

    try {
        clipboardFn();
    } finally {
        tiptapRenderer?.setAttribute('contenteditable', 'false');
        emptyCellCopyTarget?.remove();
    }
};

/**
 * Runs a copy or cut action in Capacitor, whose WebView does not dispatch the browser clipboard events that
 * Handsontable normally uses to populate the clipboard and trigger its before/after copy and cut hooks.
 */
const runWithCapacitorClipboardWorkaround = async (
    hotInstance: MNHotInstance,
    clipboardPlugin: CopyPaste,
    operation: 'copy' | 'cut',
): Promise<void> => {
    clipboardPlugin.setCopyableText();

    const copyableRanges = clipboardPlugin.copyableRanges;
    const copiedData = clipboardPlugin.getRangedData(copyableRanges);
    const clipboardText = clipboardPlugin.getRangedCopyableData(copyableRanges);

    // Capacitor does not dispatch the copy/cut event that Handsontable normally uses to run these hooks.
    // Run the lifecycle ourselves so table metadata bookkeeping and cut's read-only override are preserved.
    const beforeHook = operation === 'copy' ? 'beforeCopy' : 'beforeCut';
    if (!hotInstance.runHooks(beforeHook, copiedData, copyableRanges, { columnHeadersCount: 0 })) {
        return;
    }

    // A failed native write must not clear cut source cells.
    try {
        await writeToNativeClipboard(clipboardText);
    } catch {
        return;
    }

    if (operation === 'cut') {
        hotInstance.emptySelectedCells('CopyPaste.cut');
    }

    const afterHook = operation === 'copy' ? 'afterCopy' : 'afterCut';
    hotInstance.runHooks(afterHook, copiedData, copyableRanges, { columnHeadersCount: 0 });
};

/**
 * Runs a copy or cut action with the platform-specific clipboard handling it requires.
 */
const runTableClipboardAction = async (hotInstance: MNHotInstance, operation: 'copy' | 'cut'): Promise<void> => {
    const clipboardPlugin = getClipboardPlugin(hotInstance);
    if (!clipboardPlugin) return;

    if (isPlatformCapacitor(platformSingleton)) {
        return runWithCapacitorClipboardWorkaround(hotInstance, clipboardPlugin, operation);
    }

    const clipboardFn = () => (operation === 'copy' ? clipboardPlugin.copyCellsOnly() : clipboardPlugin.cut());

    if (isPlatformSafari(platformSingleton)) {
        return runWithSafariClipboardWorkaround(hotInstance, clipboardFn);
    }

    clipboardFn();
};

/**
 * Copies the selected table cells using Safari's clipboard workaround when needed.
 */
export const copyTableCellsFromContextMenu = async (hotInstance: MNHotInstance | null): Promise<void> => {
    if (!hotInstance) return;

    await runTableClipboardAction(hotInstance, 'copy');
};

/**
 * Cuts the selected table cells using Safari's clipboard workaround when needed.
 */
export const cutTableCellsFromContextMenu = async (hotInstance: MNHotInstance | null): Promise<void> => {
    if (!hotInstance) return;

    await runTableClipboardAction(hotInstance, 'cut');
};

/**
 * Pastes already-read clipboard text into the selected table cells, optionally waiting for the
 * invoking menu to finish closing first.
 */
const pasteClipboardTextIntoTable = async (
    clipboardPlugin: CopyPaste,
    clipboardTextPromise: Promise<string | null>,
    awaitMenuHidden?: Promise<void>,
) => {
    const clipboard = await clipboardTextPromise;
    await awaitMenuHidden;

    // Clipboard access can be unavailable or denied when invoked from a context-menu action.
    // In this case, do nothing.
    if (clipboard === null) return;

    try {
        // Handsontable ignores an empty string paste, so use an empty table cell to trigger its normal paste flow.
        if (!clipboard) return clipboardPlugin.paste('', EMPTY_CELL_PASTE_HTML);

        clipboardPlugin.paste(clipboard);
    } catch {
        // Paste failures were always swallowed for context-menu pastes; keep that failure mode.
    }
};

/**
 * Pastes the current plain-text clipboard content into the selected table cells from Handsontable's built-in menu.
 * Context-menu clicks do not dispatch a paste event with clipboard data as keyboard paste does.
 */
export const pasteTableCellsFromContextMenu = async (hotInstance: MNHotInstance | null): Promise<void> => {
    const clipboardPlugin = getClipboardPlugin(hotInstance);
    if (!hotInstance || !clipboardPlugin) return;

    // On mobile browsers and iPadOS, Handsontable delays hiding the menu to prevent the delayed click reaching content
    // beneath it. Wait for the normal close, which restores this table's listening state, before CopyPaste receives
    // the clipboard text.
    const waitAfterContextMenuHide = new Promise<void>((resolve) =>
        hotInstance.addHookOnce('afterContextMenuHide', () => resolve()),
    );

    return pasteClipboardTextIntoTable(clipboardPlugin, readTableClipboardText(), waitAfterContextMenuHide);
};

/**
 * Pastes clipboard text into the selected table cells from the Roma context menu, which runs its grid
 * actions one frame after the menu has closed. The clipboard read must start inside the menu click's
 * transient user activation, so the caller passes the pending read rather than reading here.
 */
export const pasteTableCellsFromContextMenuV2 = async (
    hotInstance: MNHotInstance | null,
    clipboardTextPromise: Promise<string | null>,
): Promise<void> => {
    const clipboardPlugin = getClipboardPlugin(hotInstance);
    if (!clipboardPlugin) return;

    return pasteClipboardTextIntoTable(clipboardPlugin, clipboardTextPromise);
};
